import React, { useEffect, useState } from 'react';
import Axios from 'axios';
import * as Routes from '@/modules/routes';
import cn from '@/utils/cn';
import type { LangfuseTrace } from './types';

interface Props {
  traceId: string;
}

const eyebrowClass = 'tw-m-0 tw-mb-1 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wider tw-text-spa-tertiary';

const codeBlockClass = cn(
  'tw-m-0 tw-max-h-48 tw-overflow-auto tw-whitespace-pre-wrap tw-break-words tw-rounded-md',
  'tw-border tw-border-solid tw-border-spa-border-secondary tw-bg-spa-bg-code tw-px-4 tw-py-3',
  'tw-font-mono tw-text-xs tw-leading-normal tw-text-spa-primary',
);

const inlineCodeClass = cn(
  'tw-rounded tw-border tw-border-solid tw-border-spa-border-input tw-bg-spa-bg-tertiary',
  'tw-px-1.5 tw-py-0.5 tw-font-mono tw-text-[0.6875rem] tw-text-spa-accent-text-hover',
);

const durationMs = (start: string, end: string | null): string => {
  if (!end) return '—';
  const ms = new Date(end).getTime() - new Date(start).getTime();
  return ms >= 1000 ? `${(ms / 1000).toFixed(2)}s` : `${ms}ms`;
};

const Stat = ({ label, value }: { label: string; value: React.ReactNode }) => (
  <div className="tw-min-w-0">
    <p className={eyebrowClass}>{label}</p>
    <p className="tw-m-0 tw-truncate tw-text-sm tw-font-medium tw-tabular-nums tw-text-spa-heading">{value}</p>
  </div>
);

const TraceDetail = ({ traceId }: Props) => {
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [trace, setTrace] = useState<LangfuseTrace | null>(null);

  useEffect(() => {
    setIsLoading(true);
    setError(null);
    Axios.get<LangfuseTrace>(Routes.show_trace_admin_langfuse_traces_path(), { params: { trace_id: traceId } })
      .then(res => setTrace(res.data))
      // The controller relays Langfuse's own failure (e.g. a 404 for a trace that was never
      // ingested), which is the part a sysadmin needs to see.
      .catch(err => setError(err.response?.data?.error || 'Failed to load trace.'))
      .finally(() => setIsLoading(false));
  }, [traceId]);

  if (isLoading) return <p className="tw-m-0 tw-py-2 tw-text-sm tw-text-spa-tertiary">Loading trace…</p>;
  if (error) return <p className="tw-m-0 tw-py-2 tw-text-sm tw-text-spa-error">{error}</p>;
  if (!trace) return null;

  // Matched by name, not by type: once the gateway's spans nest onto our trace (Bifrost::Client sends
  // a traceparent), a trace carries several GENERATION observations — key.selection and the upstream
  // provider call among them — and only this one has our input/output. The type fallback keeps
  // gateway-only orphan traces rendering something.
  const generation = trace.observations?.find(o => o.name === 'llm-generation')
    ?? trace.observations?.find(o => o.type === 'GENERATION');

  return (
    <div className="tw-flex tw-flex-col tw-gap-4 tw-py-2">
      <dl className="tw-m-0 tw-flex tw-flex-wrap tw-gap-x-8 tw-gap-y-2">
        <div>
          <dt className={eyebrowClass}>Trace ID</dt>
          <dd className="tw-m-0"><code className={inlineCodeClass}>{trace.id}</code></dd>
        </div>
        <div>
          <dt className={eyebrowClass}>Name</dt>
          <dd className="tw-m-0 tw-text-sm tw-text-spa-primary">{trace.name}</dd>
        </div>
        <div>
          <dt className={eyebrowClass}>User ID</dt>
          <dd className="tw-m-0 tw-text-sm tw-text-spa-primary">{trace.userId || '—'}</dd>
        </div>
      </dl>

      {generation && (
        <>
          <div className="tw-grid tw-grid-cols-2 tw-gap-4 sm:tw-grid-cols-4">
            <Stat label="Model" value={generation.model || '—'} />
            <Stat label="Input tokens" value={generation.usageDetails?.input ?? '—'} />
            <Stat label="Output tokens" value={generation.usageDetails?.output ?? '—'} />
            <Stat label="Duration" value={durationMs(generation.startTime, generation.endTime)} />
          </div>

          {generation.input && (
            <div>
              <p className={eyebrowClass}>Input</p>
              <pre className={codeBlockClass}>{generation.input}</pre>
            </div>
          )}

          {generation.output && (
            <div>
              <p className={eyebrowClass}>Output</p>
              <pre className={codeBlockClass}>{generation.output}</pre>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TraceDetail;
